import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, User, Mail, Phone, MapPin, Save, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { useAuth } from "../App";
import DeleteAccountSection from "../components/DeleteAccountSection";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { FormPageShell } from "../components/FormPageShell";

const ClientProfilePage = () => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation("client");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    city: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        city: user.city || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t("nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.put("/auth/profile/client", {
        name: form.name,
        phone: form.phone,
        city: form.city,
      });
      setUser({ ...user, ...res.data.user });
      toast.success(t("profileUpdated"));
    } catch (error) {
      toastApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageShell centered={false}>
      <div className="max-w-2xl mx-auto w-full">
        <Link to="/">
          <Button variant="ghost" className="mb-6" data-testid="back-to-home">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("back")}
          </Button>
        </Link>

        <h1 className="text-3xl font-bold text-slate-900 mb-8">{t("profileTitle")}</h1>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t("profileSectionTitle")}</CardTitle>
            <CardDescription>{t("profileSectionDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("nameLabel")}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t("namePlaceholder")}
                  className="pl-10 h-11 rounded-xl"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("emailLabel")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  value={user.email || ""}
                  disabled
                  className="pl-10 h-11 rounded-xl bg-slate-100"
                  data-testid="profile-email-input"
                />
              </div>
              <p className="text-xs text-slate-500">{t("emailImmutable")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("phoneOptional")}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder={t("phonePlaceholder")}
                  className="pl-10 h-11 rounded-xl"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">{t("cityOptional")}</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="city"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder={t("cityPlaceholder")}
                  className="pl-10 h-11 rounded-xl"
                  data-testid="profile-city-input"
                />
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-11 rounded-xl bg-omu-blue-500 hover:bg-omu-blue-600"
              data-testid="profile-save-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t("save")}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <DeleteAccountSection />
      </div>
    </FormPageShell>
  );
};

export default ClientProfilePage;
